'use client';

import type { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import React, { useRef } from 'react';
import { Arrow } from 'components/Arrow';
import { Portal } from 'components/Portal';
import { textStyles } from 'components/Text/text-variants';
import {
  useFloatingPosition,
  type UseFloatingPositionProps,
} from 'hooks/useFloatingPosition/useFloatingPosition';
import styled, { css } from 'styled-components';
import type { ReferenceElement } from '@floating-ui/dom';
import {
  mergeRefs,
  useEnterAnimation,
  useExitAnimation,
} from '@react-aria/utils';
import { theme } from '@frameio/vapor-theme';

/**
 * The shared tooltip positioning props that extend the base positioning props
 * with additional component-specific positioning options.
 */
export type TooltipPositioningProps = {
  /** The target element or virtual element */
  target?: ReferenceElement;
  /**
   * Offset along the main axis and cross axis
   * @example [0, 10] - 0px along main axis, 10px along cross axis
   */
  offset?: readonly [number, number];
  /** The z-index of the tooltip */
  zIndex?: number;

  // Omit the props that are internal to the positioning logic
  // but are consumed differently at the component level
} & Pick<
  UseFloatingPositionProps,
  | 'placement'
  | 'boundary'
  | 'autoHide'
  | 'fallbackPlacements'
  | 'overflowPadding'
>;

export type TooltipContentProps = {
  /** Content props */
  /** The primary label of the Tooltip. */
  title?: ReactNode | string;
  /** The secondary label of the Tooltip. */
  details?: ReactNode;
  /** An element to render before the text content. */
  adornmentBefore?: ReactNode;
  /** An element to render after the text content. */
  adornmentAfter?: ReactNode;
  /** max-width for the Tooltip container. */
  maxWidth?: string;
  /** The children of the Tooltip. */
  children?: ReactNode;

  /** Animation props */
  /** Whether or not the enter animation should play when the Tooltip mounts. */
  animateOnMount?: boolean;
  /** Whether or not the exit animation should play when the Tooltip unmounts. */
  animateOnUnmount?: boolean;

  /** State props */
  /** Whether the tooltip is showing */
  isShowing?: boolean;

  /** Rendering props */
  /** Whether to use the portal */
  shouldUsePortal?: boolean;
  /** Whether to use the positioner */
  shouldUsePositioner?: boolean;

  /** Accessibility props */
  /** ID of the tooltip */
  id?: string;
  /** The role of the tooltip */
  role?: string;

  /** @deprecated - No longer needed */
  observeContentSize?: boolean;
  /** @deprecated - No longer needed */
  observeTargetSize?: boolean;
} & TooltipPositioningProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

const defaultOffset = [0, 8] as const;

/**
 * A "dumb" version of the Tooltip which renders a Tooltip without
 * the behavior controls provided by `Tooltip`.
 */

export const TooltipContent = React.forwardRef<any, TooltipContentProps>(
  function TooltipContent(
    {
      // Content props
      adornmentBefore,
      adornmentAfter,
      title,
      details,
      children,

      // @deprecated and now unused
      observeTargetSize: __observeTargetSize,

      // State props
      isShowing,
      shouldUsePortal = false,
      animateOnMount = true,
      animateOnUnmount = true,
      offset = defaultOffset,
      ...other
    },
    ref
  ) {
    const localRef = useRef(null);

    const isExiting =
      useExitAnimation(localRef, Boolean(isShowing)) && animateOnUnmount;

    if (!isShowing && !isExiting) {
      return null;
    }

    const offsetMainAxis = offset[1];
    const offsetCrossAxis = offset[0];

    return (
      <Portal disabled={!shouldUsePortal}>
        <PositionedTooltip
          ref={mergeRefs(localRef, ref)}
          animateOnMount={animateOnMount}
          isExiting={isExiting}
          offsetCrossAxis={offsetCrossAxis}
          offsetMainAxis={offsetMainAxis}
          {...other}
        >
          {children || (
            <Content>
              {adornmentBefore && <Adornment>{adornmentBefore}</Adornment>}
              <TextContent>
                {typeof title === 'string' ? (
                  <TooltipTitle>{title}</TooltipTitle>
                ) : (
                  title
                )}
                {details && <TooltipDetails>{details}</TooltipDetails>}
              </TextContent>
              {adornmentAfter && <Adornment>{adornmentAfter}</Adornment>}
            </Content>
          )}
        </PositionedTooltip>
      </Portal>
    );
  }
);

/**
 * Renders the Tooltip content with arrow, and positions it
 * relative to the target element.
 */

type PositionedTooltipProps = PropsWithChildren<
  {
    /** Whether the tooltip is exiting */
    isExiting?: boolean;
    /** Whether to use the positioner */
    shouldUsePositioner?: boolean;
    /** Whether to animate on mount */
    animateOnMount?: boolean;
    /** Offset along the main axis */
    offsetMainAxis?: number;
    /** Offset along the cross axis */
    offsetCrossAxis?: number;
    /** max-width for the Tooltip container. */
    maxWidth?: string;
  } & TooltipPositioningProps
>;

const PositionedTooltip = React.forwardRef<
  HTMLDivElement,
  PositionedTooltipProps
>(
  (
    {
      placement = 'top',
      offsetMainAxis,
      isExiting,
      offsetCrossAxis,
      shouldUsePositioner = false,
      target,
      animateOnMount,
      children,
      maxWidth = '296px',
      boundary,
      autoHide = true,
      fallbackPlacements,
      zIndex = theme.zIndices.tooltip,
      ...other
    },
    ref
  ) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const arrowRef = useRef<HTMLDivElement>(null);

    const { placement: floatingUIPlacement, referenceHidden } =
      useFloatingPosition(target, tooltipRef, {
        enabled: shouldUsePositioner,
        placement,
        arrowRef,
        offsetMainAxis,
        offsetCrossAxis,
        boundary,
        autoHide,
        fallbackPlacements,
      });

    const resolvedPlacement = shouldUsePositioner
      ? floatingUIPlacement
      : placement;

    const isEntering =
      useEnterAnimation(tooltipRef, Boolean(resolvedPlacement)) &&
      animateOnMount;

    // This state occurs when `isShowing` is true but `target` is not available,
    // which can occur when a Tooltip is controlled and has `isShowing`
    // set to true on mount. Let's just unmount the tooltip for now to match
    // existing behavior.
    if (!target && shouldUsePositioner) {
      return null;
    }

    return (
      <TooltipContainer
        ref={mergeRefs(tooltipRef, ref)}
        data-entering={isEntering || undefined}
        data-exiting={isExiting || undefined}
        data-position={resolvedPlacement || undefined}
        data-testid="tooltip-container"
        style={
          {
            zIndex,
            display: referenceHidden ? 'none' : undefined,
            '--tooltip-max-width': maxWidth,
          } as React.CSSProperties
        }
        {...other}
      >
        <Arrow ref={arrowRef} position={resolvedPlacement || placement} />
        {children}
      </TooltipContainer>
    );
  }
);

export const tooltipFadeAnimation = css`
  opacity: 1;
  transform: scale(1);
  transition-duration: 200ms;
  transition-property: opacity, transform;
  transition-timing-function: ${theme.transitions.eases.primary};
  visibility: visible;

  &:not([data-position]) {
    visibility: hidden;
    transform: scale(1);
  }

  &[data-entering] {
    transform: scale(0.9);
    opacity: 0;
    visibility: visible;
  }

  &[data-exiting] {
    transition-duration: 100ms;
    transform: scale(1);
    opacity: 0;
  }

  &[data-position='bottom'] {
    transform-origin: 50% 0;
  }

  &[data-position='bottom-start'] {
    transform-origin: 0 0;
  }

  &[data-position='bottom-end'] {
    transform-origin: 100% 0;
  }

  &[data-position='top'] {
    transform-origin: 50% 100%;
  }

  &[data-position='top-start'] {
    transform-origin: 0 100%;
  }

  &[data-position='top-end'] {
    transform-origin: 100% 100%;
  }

  &[data-position='right'] {
    transform-origin: 0 50%;
  }

  &[data-position='right-start'] {
    transform-origin: 0 0;
  }

  &[data-position='right-end'] {
    transform-origin: 0 100%;
  }

  &[data-position='left'] {
    transform-origin: 100% 50%;
  }

  &[data-position='left-start'] {
    transform-origin: 100% 0;
  }

  &[data-position='left-end'] {
    transform-origin: 100% 100%;
  }
`;

export const tooltipStyles = css`
  background: ${theme.colors.components.tooltip.background};
  border: 1px solid ${theme.colors.border.tertiary};
  border-radius: ${theme.radii[8]};
  box-shadow: ${theme.shadows['8dp']};
  display: inline-block;
  max-width: var(--tooltip-max-width, 296px);
  overflow-wrap: break-word;
  padding: ${theme.spaces[8]} ${theme.spaces[12]};
  position: relative;
  text-align: center;
  width: max-content;
`;

export const TooltipContainer = styled.div`
  ${tooltipStyles};
  ${tooltipFadeAnimation};
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spaces[12]};
`;

const Adornment = styled.div`
  flex: 0 0 auto;
`;

const TextContent = styled.div`
  flex: 1 1 auto;
`;

export const TooltipTitle = styled.div`
  ${textStyles.secondary};
  color: ${theme.colors.text.primary};
  overflow-wrap: break-word;
`;

export const TooltipDetails = styled.div`
  ${textStyles.secondary};
  color: ${theme.colors.text.secondary};
  overflow-wrap: break-word;
`;
